import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, auth, db } from "./firebase";
import type { UserDoc } from "./lib/types";
import { tenantPaths, type TenantPaths } from "./lib/paths";
import { ensureSession, trackSession } from "./lib/session";

const EMAIL_KEY = "recipe-sop:signin-email";

const acceptInvite = httpsCallable<void, { status: string; tenantId?: string }>(
  getFunctions(app, "asia-northeast1"),
  "acceptInvite",
);

/**
 * 所属テナントをクレームから取る。無ければ招待を受けてみる（functions/users.mjs）。
 *
 * 管理者がメールアドレスで招待していれば、ここでクレームが付く。クレームは
 * トークンを取り直すまで反映されないので、受けたら強制的に取り直す
 * （再ログインを頼まずに済む）。招待が無ければ null のまま＝登録待ち。
 */
async function resolveTenant(u: User): Promise<string | null> {
  const read = async (force: boolean) => {
    const token = await u.getIdTokenResult(force);
    const claim = token.claims.tenantId;
    return typeof claim === "string" && claim ? claim : null;
  };
  const tid = await read(false);
  if (tid) return tid;
  try {
    const r = await acceptInvite();
    if (r.data.status !== "joined") return null;
  } catch (e) {
    console.warn("招待の確認に失敗", e);
    return null;
  }
  const joined = await read(true);
  // 閲覧プロキシのセッションは、テナントが付く前のトークンで張られている。
  // そのままだとマニュアルが 403（テナント未割当）になるので、取り直したトークンで張り直す
  trackSession(ensureSession(u)).catch((e) => console.warn("セッションの確立に失敗", e));
  return joined;
}

interface AuthState {
  user: User | null;
  profile: UserDoc | null;
  /** 認証状態とプロフィールの両方が確定するまで true */
  loading: boolean;
  /** 認証は通ったが users/{uid} が無い = 管理者による登録待ち */
  needsProvisioning: boolean;
  /**
   * 所属テナント。カスタムクレーム tenantId から取る。
   *
   * role や担当品目を Firestore に置いているのは付け替えの即時反映が要るためだが、
   * 所属テナントは動かないのでクレームでよい。クレームが無い = どのテナントにも
   * 属していない（= 登録待ち）として扱う。
   */
  tenantId: string | null;
  /** テナント配下のパス生成。tenantId が無いときは null */
  paths: TenantPaths | null;
  signInWithGoogle: () => Promise<void>;
  sendLink: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth は AuthProvider の内側で使ってください");
  return v;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsProvisioning, setNeedsProvisioning] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);

  // メールリンクで戻ってきた場合のサインイン完了処理
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const stored = window.localStorage.getItem(EMAIL_KEY);
    // 別端末で開かれた場合は localStorage に無いので聞き直す
    const email = stored ?? window.prompt("確認のため、リンクを送ったメールアドレスを入力してください") ?? "";
    if (!email) return;
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_KEY);
        // URL からワンタイムのリンク情報を消す(履歴に残さない)
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((e) => console.error("サインインに失敗しました", e));
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setProfile(null);
        setNeedsProvisioning(false);
        setTenantId(null);
        setLoading(false);
        return;
      }
      // 閲覧プロキシ(§4.1)のセッションを張る。失敗しても画面自体は使えるので止めない
      // (SOP を開いたときに 401 で気付ける)。
      // 完了を trackSession に預け、プロキシから取る画面は waitForSession で待つ。
      trackSession(ensureSession(u)).catch((e) => console.warn("セッションの確立に失敗", e));

      // 役割と担当品目は users/{uid} が正。無ければ未登録として扱う
      // (自己登録させない。権限の付与は管理者の操作に限る)
      // 所属テナントを先に決める。これが無いとどのパスも組み立てられない
      let tid: string | null = null;
      try {
        tid = await resolveTenant(u);
      } catch {
        tid = null;
      }
      setTenantId(tid);
      if (!tid) {
        // テナント未割当 = 管理者による登録待ち（自己登録させない）
        setProfile(null);
        setNeedsProvisioning(true);
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(tenantPaths(db, tid).userDoc(u.uid));
        if (snap.exists()) {
          setProfile(snap.data() as UserDoc);
          setNeedsProvisioning(false);
        } else {
          setProfile(null);
          setNeedsProvisioning(true);
        }
      } catch {
        // ルールで弾かれた場合も未登録扱い(読めない = 権限が無い)
        setProfile(null);
        setNeedsProvisioning(true);
      }
      setLoading(false);
    });
  }, []);

  // 既定の方式。sakama.tokyo は Google Workspace なので配信の失敗が原理的に無い。
  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    await signInWithPopup(auth, provider);
  }

  // 代替。Google アカウントを持たない利用者向けに残すが、
  // 既定メーラーの配信が不安定なため主経路にはしない。
  async function sendLink(email: string) {
    await sendSignInLinkToEmail(auth, email, {
      url: window.location.origin,
      handleCodeInApp: true,
    });
    window.localStorage.setItem(EMAIL_KEY, email);
  }

  // テナントが決まったときだけ作り直す。画面の読み込み処理は paths を依存に持つので、
  // 描画のたびに新しいオブジェクトを渡すと購読を張り直し続けてしまう
  const paths = useMemo(() => (tenantId ? tenantPaths(db, tenantId) : null), [tenantId]);

  return (
    <Ctx.Provider
      value={{
        user,
        profile,
        loading,
        needsProvisioning,
        tenantId,
        paths,
        signInWithGoogle,
        sendLink,
        logout: () => signOut(auth),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
